/**
 * The "Admin" menu — managing the studio itself. See
 * lib/navigation/nav-manifest.js for the entry shape and the vocabulary
 * `requires` draws from.
 *
 * Note this menu is never trimmed by a studio's `menuOverride` allow-list (see
 * `overrideExempt` on its manifest entry): it is where a stripped studio is
 * reconfigured from.
 */

/** @type {import("@/lib/navigation/nav-manifest").NavManifestItem[]} */
export const ADMIN_ITEMS = [
  {
    label: "Studio",
    iconName: "HiOutlineCog8Tooth",
    requires: ["workspace", "adminOrSupervisorOrLeader"],
    subItems: [
      // Studio Settings is two entries because it is two destinations: admins
      // get the settings screen, everyone else lands on the one admin page
      // their role can open.
      {
        href: "/workspaces/{workspaceId}/edit",
        label: "Studio Settings",
        iconName: "HiOutlineCog8Tooth",
        requires: "admin",
      },
      {
        href: "/workspaces/admin/people",
        label: "Studio Settings",
        iconName: "HiOutlineCog8Tooth",
        requires: "notAdmin",
      },
      {
        href: "/workspaces/{workspaceId}/positioning",
        label: "Positioning",
        iconName: "IoFlagOutline",
        requires: "admin",
      },
    ],
  },
  {
    label: "Content",
    iconName: "IoDocumentTextOutline",
    requires: ["workspace", "admin"],
    subItems: [
      {
        href: "/workspaces/admin/data/content",
        label: "Content",
        iconName: "IoDocumentTextOutline",
      },
      {
        href: "/workspaces/admin/glossary",
        label: "Glossary",
        iconName: "IoBookOutline",
      },
      {
        href: "/workspaces/admin/faqs",
        label: "Q&As",
        iconName: "IoHelpCircleOutline",
      },
      {
        href: "/workspaces/admin/fbiv",
        label: "Capability Maps",
        iconName: "RxTarget",
      },
      {
        href: "/workspaces/admin/takeaway-reports",
        label: "Takeaway Reports",
        iconName: "AiOutlineHighlight",
      },
    ],
  },
  {
    label: "People & Training",
    iconName: "IoPeopleOutline",
    requires: ["workspace", "adminOrSupervisorOrLeader"],
    subItems: [
      {
        href: "/workspaces/admin/people",
        label: "People",
        iconName: "IoPeopleOutline",
      },
      {
        href: "/workspaces/admin/people/communities",
        label: "Communities",
        iconName: "IoPeopleOutline",
        requires: "supervisorOrAbove",
      },
      {
        href: "/workspaces/admin/role-plays",
        label: "Assignments",
        iconName: "ClipboardList",
      },
      {
        href: "/workspaces/admin/learning-paths",
        label: "Learning Paths",
        iconName: "SlGraduation",
        requires: "supervisorOrAbove",
      },
      {
        href: "/workspaces/admin/tests",
        label: "Tests",
        iconName: "IoDocumentTextOutline",
      },
      {
        href: "/workspaces/admin/faq-tests",
        label: "Q&A Tests",
        iconName: "IoHelpCircleOutline",
      },
      {
        href: "/workspaces/admin/glossary-tests",
        label: "Glossary Tests",
        iconName: "IoBookOutline",
      },
      {
        href: "/workspaces/admin/fbiv-tests",
        label: "Capability Map Tests",
        iconName: "RxTarget",
      },
    ],
  },
  {
    label: "Performance",
    iconName: "RxTarget",
    requires: ["workspace", "adminOrSupervisorOrLeader"],
    subItems: [
      {
        href: "/workspaces/admin/okrs",
        label: "OKRs",
        iconName: "RxTarget",
      },
      {
        href: "/workspaces/admin/badges",
        label: "Badges",
        iconName: "IoRibbonOutline",
        requires: "badges",
      },
    ],
  },
  {
    label: "Insights",
    iconName: "IoBarChartOutline",
    requires: ["workspace", "supervisorOrAbove"],
    subItems: [
      {
        href: "/workspaces/admin/analytics",
        label: "Analytics",
        iconName: "IoBarChartOutline",
      },
    ],
  },
  {
    label: "Module Configuration",
    iconName: "HiOutlineCog8Tooth",
    requires: ["workspace", "admin"],
    subItems: [
      {
        href: "/workspaces/admin/data/modules",
        label: "Modules",
        iconName: "HiOutlineCog8Tooth",
      },
      {
        href: "/workspaces/admin/data/personas",
        label: "Personas",
        iconName: "IoPersonOutline",
      },
      {
        href: "/workspaces/admin/data/rubrics",
        label: "Rubrics",
        iconName: "IoListOutline",
      },
      {
        href: "/workspaces/admin/data/stories",
        label: "Stories",
        iconName: "IoBookOutline",
      },
      {
        href: "/workspaces/admin/data/discovery",
        label: "Discovery",
        iconName: "CiMap",
      },
      {
        href: "/workspaces/admin/data/pitch",
        label: "Messaging / Pitch",
        iconName: "HiOutlinePresentationChartBar",
      },
      {
        href: "/workspaces/admin/data/cold-call",
        label: "Outbound & Inbounds",
        iconName: "IoCallOutline",
      },
      {
        href: "/workspaces/admin/data/objections",
        label: "Objections",
        iconName: "HiNoSymbol",
      },
      {
        href: "/workspaces/admin/data/plays",
        label: "Plays",
        iconName: "LiaTheaterMasksSolid",
      },
      {
        href: "/workspaces/admin/phone-practice",
        label: "Phone Practice",
        iconName: "IoCallOutline",
        requires: "phonePractice",
      },
      {
        href: "/dem-cog/dems",
        label: "Expert Models (DEMs)",
        iconName: "ClipboardList",
        requires: "dealClinic",
      },
      {
        href: "/workspaces/admin/data/management-scenarios",
        label: "Management",
        iconName: "IoPersonOutline",
        requires: "management",
      },
      {
        href: "/workspaces/admin/data/seller-personas",
        label: "Other Personas",
        iconName: "IoPeopleOutline",
        requires: "management",
      },
    ],
  },
  {
    href: "/workspaces/broadcast",
    label: "Send Broadcast",
    iconName: "FaBroadcastTower",
    requires: ["workspace", "admin"],
  },
  {
    href: "/help/admin",
    label: "Help",
    iconName: "IoHelpCircleOutline",
    requires: ["workspace", "supervisorOrAbove"],
  },
];
